import * as THREE from 'three';
import { ball, box, leaf, mesh, random, tube } from './art';

type CanalProp = { kind: 'bike' | 'planter' | 'boat' | 'reeds'; x: number; z: number; angle: number; scale: number; color: string };
const layouts = new Map<number, CanalProp[]>();
export function canalLayout(seed: number): CanalProp[] {
  const cached = layouts.get(seed); if (cached) return cached;
  const r = random(seed ^ 0x4c414e); const props: CanalProp[] = [];
  const protectedPoints = [[168, -168], [264, -120], [408, -168], [364, -108], [198, -154], [444, -80]];
  const palette = ['#4d8f91', '#b26368', '#e3bb71', '#597b69'];
  for (let attempt = 0; attempt < 600 && props.filter(p => p.kind !== 'boat').length < 38; attempt++) {
    const x = 152 + r() * 368; const z = -184 + r() * 128;
    const dx = Math.abs(x - (Math.floor(x / 48) * 48 + 24));
    const dz = Math.abs(z - (Math.floor(z / 48) * 48 + 24));
    if (dx < 11 || dz < 10 || Math.abs(z + 120) < 11 || protectedPoints.some(([px, pz]) => Math.hypot(x - px, z - pz) < 10) || props.some(p => Math.hypot(x - p.x, z - p.z) < 12)) continue;
    const kind = r() < .3 ? 'bike' : r() < .5 ? 'planter' : 'reeds';
    props.push({ kind, x, z, angle: r() * Math.PI * 2, scale: .75 + r() * .35, color: palette[Math.floor(r() * palette.length)] });
  }
  for (let attempt = 0; attempt < 200 && props.filter(p => p.kind === 'boat').length < 5; attempt++) {
    const x = 156 + r() * 360;
    if (Array.from({ length: 8 }, (_, i) => 168 + i * 48).some(b => Math.abs(x - b) < 12) || Math.abs(x - 286) < 18 || props.some(p => p.kind === 'boat' && Math.abs(x - p.x) < 23)) continue;
    props.push({ kind: 'boat', x, z: -120 + (r() - .5) * 1.4, angle: (r() - .5) * .14, scale: .65 + r() * .3, color: palette[Math.floor(r() * palette.length)] });
  }
  if (layouts.size > 8) layouts.clear(); layouts.set(seed, props); return props;
}

export function canalBlocked(x: number, z: number, seed = 42): boolean {
  return canalLayout(seed).some(p => (p.kind === 'bike' || p.kind === 'planter') && Math.hypot(x - p.x, z - p.z) < (p.kind === 'bike' ? 6.2 : 3.5) * p.scale);
}

export function buildCanal(g: THREE.Group, x: number, z: number, waterRow: boolean, seed: number): void {
  // Instanced paving keeps the streamed quay affordable in both camera views.
  const paving = new THREE.InstancedMesh(new THREE.BoxGeometry(5.8, .06, 2.8), new THREE.MeshStandardMaterial({ color: '#b7b7ad', roughness: .95 }), 128);
  const matrix = new THREE.Matrix4(); let count = 0;
  for (let row = 0; row < 16; row++) for (let col = 0; col < 8; col++) {
    const pz = z - 22.5 + row * 3;
    if (waterRow && Math.abs(pz + 120) < 7) continue;
    matrix.makeTranslation(x - 21 + col * 6 + (row % 2 ? .35 : 0), .04, pz);
    paving.setMatrixAt(count++, matrix);
  }
  paving.count = count; paving.receiveShadow = true; g.add(paving);
  for (const p of canalLayout(seed)) {
    if (Math.floor(p.x / 48) !== Math.floor(x / 48) || Math.floor(p.z / 48) !== Math.floor(z / 48)) continue;
    const prop = new THREE.Group(); prop.position.set(p.x, 0, p.z); prop.rotation.y = p.angle; prop.scale.setScalar(p.scale); prop.name = `canal-${p.kind}`; g.add(prop);
    if (p.kind === 'bike') bicycle(prop, 0, 0, p.color);
    else if (p.kind === 'boat') boat(prop, 0, 0, p.color);
    else if (p.kind === 'planter') planter(prop, 0, 0);
    else for (let i = 0; i < 7; i++) { const h = 2 + i % 4; tube(prop, [new THREE.Vector3(i * .4 - 1, 0, 0), new THREE.Vector3(i * .5 - 1, h, .4)], .07, '#688761'); leaf(prop, i * .4 - 1, h * .5, 0, 2, .3, '#87a477', i); }
  }
  if (!waterRow) return;
  const water = mesh(new THREE.PlaneGeometry(48, 12), '#79bac0', g, x, .08, -120); water.rotation.x = -Math.PI / 2; water.castShadow = false;
  for (const side of [-1, 1]) {
    box(g, x, .22, -120 + side * 6.4, 48, .44, .8, '#d3d0bf');
    for (let i = 0; i < 12; i++) box(g, x - 22 + i * 4, .27, -120 + side * 6.85, 3.8, .5, .25, '#9c8e82');
  }
  const index = Math.round((x - 168) / 48);
  const railColor = index % 3 === 0 ? '#78695b' : '#466367';
  box(g, x, .38, -120, 4.8, .25, 14, index % 2 ? '#b5b4a9' : '#d1cbb8').name = 'canal-stone-bridge';
  for (const side of [-1, 1]) {
    const points = Array.from({ length: 17 }, (_, i) => new THREE.Vector3(x + side * 2.25, 1.7 + Math.sin(i / 16 * Math.PI) * (index % 3 === 1 ? .4 : 1.3), -127 + i * 14 / 16));
    tube(g, points, .095, railColor);
    for (let i = 0; i < 9; i++) {
      const h = 1.2 + Math.sin(i / 8 * Math.PI) * (index % 3 === 1 ? .4 : 1.3);
      mesh(new THREE.CylinderGeometry(.055, .055, h, 6), railColor, g, x + side * 2.25, .5 + h / 2, -127 + i * 1.75);
    }
  }
}

function planter(g: THREE.Group, x: number, z: number): void {
  box(g, x, .8, z, 5.4, 1.6, 3, '#9e655b');
  box(g, x, 1.63, z, 5, .12, 2.6, '#695f55');
  for (let i = 0; i < 5; i++) {
    const fx = x - 2 + i;
    leaf(g, fx, 2, z, 2.4, .6, '#658b64', i * 1.3);
    mesh(new THREE.CylinderGeometry(.05, .07, 2, 6), '#5c8159', g, fx, 2.5, z);
    for (let p = 0; p < 5; p++) ball(g, fx + Math.cos(p * 1.256) * .28, 3.5, z + Math.sin(p * 1.256) * .28, .23, .16, .23, i % 2 ? '#eab2c4' : '#e5cf80');
  }
}

function bicycle(g: THREE.Group, x: number, z: number, color: string): void {
  for (const dx of [-3.6, 3.6]) {
    mesh(new THREE.TorusGeometry(2.2, .16, 8, 24), '#465a59', g, x + dx, 2.3, z);
    for (let i = 0; i < 6; i++) {
      const a = i * Math.PI / 3;
      tube(g, [new THREE.Vector3(x + dx, 2.3, z), new THREE.Vector3(x + dx + Math.cos(a) * 2.1, 2.3 + Math.sin(a) * 2.1, z)], .035, '#a5b4af');
    }
  }
  const v = (dx: number, y: number) => new THREE.Vector3(x + dx, y, z);
  for (const points of [[v(-3.6, 2.3), v(-1, 4.6), v(.2, 2.3), v(-3.6, 2.3)], [v(-1, 4.6), v(2.1, 4.6), v(.2, 2.3)], [v(.2, 2.3), v(3.6, 2.3), v(2.1, 5.3), v(2.6, 5.8)]]) tube(g, points, .12, color);
  box(g, x - 1, 4.85, z, 1.6, .24, .65, '#685c54');
  tube(g, [new THREE.Vector3(x + 2.6, 5.8, z - .7), v(2.6, 5.8), new THREE.Vector3(x + 2.6, 5.8, z + .7)], .12, '#465a59');
}

function boat(g: THREE.Group, x: number, z: number, color: string): void {
  const hull = ball(g, x, .6, z, 7, .6, 2.2, color); hull.name = 'canal-moored-boat';
  ball(g, x, 1.22, z, 5.7, .06, 1.7, '#566567');
  for (const dx of [-3, 0, 3]) box(g, x + dx, 1.4, z, .5, .2, 3.4, '#f0dcc0');
  for (const dx of [-3.5, 3.5]) {
    mesh(new THREE.CylinderGeometry(.2, .25, 1.5, 8), '#695f55', g, x + dx, .8, z + 6.7);
    tube(g, [new THREE.Vector3(x + dx, 1.25, z + 1.8), new THREE.Vector3(x + dx, .9, z + 4), new THREE.Vector3(x + dx, 1.5, z + 6.7)], .055, '#d7cdb4');
  }
}
